/**
 * ComingSoon.jsx - Maintenance Page
 * 
 * Bilingual support (Arabic & English)
 * Staff access control with email verification
 * Compatible with all browsers (Chrome, Firefox, Edge)
 */

import React, { useState, useEffect } from 'react';
import { Globe, ChevronDown, Check, X, Lock, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [language, setLanguage] = useState('ar');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffEmail, setStaffEmail] = useState('');
  const [staffError, setStaffError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const isArabic = language === 'ar';

  // Translations
  const translations = {
    ar: {
      logoText: 'oeia.io',
      tagline: 'Monitor Plus Platform',
      maintenance: 'للصيانة',
      comingSoon: 'قريباً',
      description: 'نحن نعمل على شيء مذهل! منصة تحليل وتسويق متكاملة',
      description2: 'ستكون متاحة قريباً',
      days: 'يوم',
      hours: 'ساعة',
      minutes: 'دقيقة',
      seconds: 'ثانية',
      emailPlaceholder: 'أدخل بريدك الإلكتروني للإشعار',
      notifyBtn: 'إشعاري',
      thankYou: 'شكراً لك! سنقوم بإشعارك عند الإطلاق',
      feature1Title: 'تحليل متقدم',
      feature1Desc: 'تحليل بيانات الأعضاء والمجموعات بذكاء اصطناعي',
      feature2Title: 'تسويق ذكي',
      feature2Desc: 'أدوات تسويق متكاملة للوصول لجمهورك المستهدف',
      feature3Title: 'أمان عالي',
      feature3Desc: 'حماية كاملة لبياناتك وخصوصيتك',
      footer: '© 2026 oeia.io - جميع الحقوق محفوظة',
      staffLogin: 'دخول الموظفين',
      selectLanguage: 'اختر اللغة',
      staffModalTitle: 'دخول الموظفين',
      staffModalDesc: 'أدخل بريدك الإلكتروني للتحقق من صلاحية الدخول',
      staffEmailPlaceholder: 'البريد الإلكتروني',
      staffVerifyBtn: 'تحقق',
      staffNotAllowed: 'عذراً، هذا البريد غير مصرح له بالدخول',
      staffChecking: 'جاري التحقق...',
      close: 'إغلاق'
    },
    en: {
      logoText: 'oeia.io',
      tagline: 'Monitor Plus Platform',
      maintenance: 'Under Maintenance',
      comingSoon: 'Coming Soon',
      description: 'We are working on something amazing! An integrated analytics and marketing platform',
      description2: 'will be available soon',
      days: 'Days',
      hours: 'Hours',
      minutes: 'Minutes',
      seconds: 'Seconds',
      emailPlaceholder: 'Enter your email for notification',
      notifyBtn: 'Notify Me',
      thankYou: 'Thank you! We will notify you when we launch',
      feature1Title: 'Advanced Analytics',
      feature1Desc: 'AI-powered member and group data analysis',
      feature2Title: 'Smart Marketing',
      feature2Desc: 'Integrated marketing tools to reach your target audience',
      feature3Title: 'High Security',
      feature3Desc: 'Complete protection for your data and privacy',
      footer: '© 2026 oeia.io - All rights reserved',
      staffLogin: 'Staff Login',
      selectLanguage: 'Select Language',
      staffModalTitle: 'Staff Login',
      staffModalDesc: 'Enter your email to verify access',
      staffEmailPlaceholder: 'Email address',
      staffVerifyBtn: 'Verify',
      staffNotAllowed: 'Sorry, this email is not authorized to access',
      staffChecking: 'Checking...',
      close: 'Close'
    }
  };

  const t = translations[language];

  const languages = [
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  const currentLang = languages.find(l => l.code === language);

  // Countdown timer
  const launchDate = new Date('2026-04-01T00:00:00');

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = launchDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
    }
  };

  const handleStaffLoginClick = () => {
    setShowStaffModal(true);
    setStaffEmail('');
    setStaffError('');
  };

  const handleStaffVerify = async (e) => {
    e.preventDefault();
    if (!staffEmail) return;

    setIsChecking(true);
    setStaffError('');

    try {
      const response = await axios.post(`${API_URL}/api/check-staff-access?email=${encodeURIComponent(staffEmail)}`);
      
      if (response.data.allowed) {
        // Try to set sessionStorage, but continue even if it fails (Edge compatibility)
        try {
          sessionStorage.setItem('maintenance_bypass', 'true');
          sessionStorage.setItem('verified_staff_email', staffEmail.toLowerCase());
        } catch (storageError) {
          // sessionStorage not available, use URL parameter instead
          console.log('sessionStorage not available, using URL fallback');
        }
        // Redirect with fallback parameter
        window.location.href = '/login?staff_bypass=true&email=' + encodeURIComponent(staffEmail.toLowerCase());
      } else {
        setStaffError(t.staffNotAllowed);
      }
    } catch (error) {
      setStaffError(t.staffNotAllowed);
    } finally {
      setIsChecking(false);
    }
  };

  // CSS animations as inline styles for better browser compatibility
  const fadeInStyle = {
    animation: 'fadeIn 0.8s ease-out forwards',
    opacity: 0
  };

  const slideUpStyle = {
    animation: 'slideUp 0.6s ease-out forwards',
    opacity: 0
  };

  return (
    <>
      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.2; }
        }
        .animate-fade-in { animation: fadeIn 0.8s ease-out forwards; }
        .animate-slide-up { animation: slideUp 0.6s ease-out forwards; }
        .animate-pulse-slow { animation: pulse 4s ease-in-out infinite; }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
      `}</style>

      <div 
        className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4 overflow-hidden relative"
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        {/* Staff Login Modal */}
        {showStaffModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in"
            onClick={() => setShowStaffModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-800 border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl animate-slide-up"
              dir={isArabic ? 'rtl' : 'ltr'}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Lock size={20} className="text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{t.staffModalTitle}</h3>
                </div>
                <button
                  onClick={() => setShowStaffModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Body */}
              <p className="text-gray-400 mb-6">{t.staffModalDesc}</p>

              <form onSubmit={handleStaffVerify}>
                <div className="relative mb-4">
                  <Mail size={18} className={`absolute top-1/2 -translate-y-1/2 ${isArabic ? 'right-4' : 'left-4'} text-gray-400`} />
                  <input
                    type="email"
                    value={staffEmail}
                    onChange={(e) => setStaffEmail(e.target.value)}
                    placeholder={t.staffEmailPlaceholder}
                    className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 bg-slate-700/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all`}
                    dir="ltr"
                    data-testid="staff-email-input"
                    disabled={isChecking}
                  />
                </div>

                {/* Error Message */}
                {staffError && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/40 rounded-xl text-red-400 text-sm text-center animate-fade-in">
                    {staffError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isChecking || !staffEmail}
                  data-testid="staff-verify-btn"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all"
                >
                  {isChecking ? t.staffChecking : t.staffVerifyBtn}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Language Toggle */}
        <div className={`absolute top-6 ${isArabic ? 'left-6' : 'right-6'} z-50`}>
          <div className="relative">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-300"
              data-testid="lang-toggle-btn"
            >
              <Globe size={18} className="text-cyan-400" />
              <span className="text-white font-medium">{currentLang?.flag} {currentLang?.code.toUpperCase()}</span>
              <ChevronDown size={16} className={`text-gray-400 transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
            </button>

            {showLangMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowLangMenu(false)} />
                <div className="absolute top-full mt-2 right-0 z-50 min-w-[160px] py-2 rounded-xl bg-slate-800/95 backdrop-blur-sm border border-white/10 shadow-xl">
                  <div className="px-3 py-2 border-b border-white/10">
                    <p className="text-xs text-gray-500 uppercase">{t.selectLanguage}</p>
                  </div>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setShowLangMenu(false);
                      }}
                      data-testid={`lang-${lang.code}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all hover:bg-white/10 ${
                        language === lang.code ? 'bg-cyan-500/10 text-cyan-400' : 'text-white'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm font-medium">{lang.name}</span>
                      {language === lang.code && <Check size={14} className="text-cyan-400 ml-auto" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse-slow delay-200"></div>
        </div>

        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        ></div>

        {/* Main Content */}
        <div className="relative z-10 text-center max-w-4xl mx-auto">
          {/* Logo */}
          <div className="mb-8 animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              {t.logoText}
            </h1>
            <p className="text-gray-400 mt-2 text-lg">{t.tagline}</p>
          </div>

          {/* Maintenance + Coming Soon */}
          <div className="mb-12 animate-slide-up delay-100">
            {/* Maintenance Badge */}
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-amber-500/20 border border-amber-500/40 rounded-full text-amber-400 text-sm font-medium">
                🔧 {t.maintenance}
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
              {t.comingSoon}
            </h2>
            <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto">
              {t.description}
              <br />
              {t.description2}
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="grid grid-cols-4 gap-4 md:gap-8 mb-12 max-w-lg mx-auto animate-slide-up delay-200">
            {[
              { value: timeLeft.days, label: t.days },
              { value: timeLeft.hours, label: t.hours },
              { value: timeLeft.minutes, label: t.minutes },
              { value: timeLeft.seconds, label: t.seconds }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-white/10"
              >
                <div className="text-3xl md:text-5xl font-bold text-white">
                  {String(item.value).padStart(2, '0')}
                </div>
                <div className="text-gray-400 text-xs md:text-sm mt-1">
                  {item.label}
                </div>
              </div>
            ))}
          </div>

          {/* Email Subscription */}
          <div className="mb-12 animate-slide-up delay-300">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  className="flex-1 px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                  dir={isArabic ? 'rtl' : 'ltr'}
                  data-testid="email-input"
                />
                <button
                  type="submit"
                  data-testid="notify-btn"
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-purple-500/25"
                >
                  {t.notifyBtn}
                </button>
              </form>
            ) : (
              <div className="text-green-400 text-lg animate-fade-in">
                ✓ {t.thankYou}
              </div>
            )}
          </div>

          {/* Features Preview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 animate-slide-up delay-400">
            {[
              { icon: '📊', title: t.feature1Title, desc: t.feature1Desc },
              { icon: '🚀', title: t.feature2Title, desc: t.feature2Desc },
              { icon: '🔒', title: t.feature3Title, desc: t.feature3Desc }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Social Links */}
          <div className="flex justify-center gap-6 animate-slide-up delay-500">
            {[
              { name: 'Telegram', icon: '📨', url: '#' },
              { name: 'Twitter', icon: '🐦', url: '#' },
              { name: 'Email', icon: '✉️', url: 'mailto:support@oeia.io' }
            ].map((social, index) => (
              <a
                key={index}
                href={social.url}
                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-xl hover:bg-white/20 transition-all"
                title={social.name}
              >
                {social.icon}
              </a>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-12">
            <p className="text-gray-500 text-sm">{t.footer}</p>
            <button 
              onClick={handleStaffLoginClick}
              className="text-gray-600 text-xs mt-4 inline-block hover:text-gray-400 transition-colors cursor-pointer"
              data-testid="staff-login-btn"
            >
              {t.staffLogin}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComingSoon;
